import CobraSpinner from '../shared/CobraSpinner';
import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Flex,
  Heading,
  HStack,
  VStack,
  Text,
  Button,
  Card,
  CardBody,
  CardHeader,
  SimpleGrid,
  Input,
  InputGroup,
  InputLeftElement,
  Textarea,
  Select,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useToast,
  
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  Divider,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useColorModeValue,
  useBreakpointValue,
  Container,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Switch,
  FormHelperText
} from '@chakra-ui/react';
import {
  SearchIcon,
  AddIcon,
  EditIcon,
  DeleteIcon,
  ViewIcon,
  TimeIcon,
  CheckCircleIcon,
  WarningIcon,
  ChevronDownIcon
} from '@chakra-ui/icons';
import {
  FiMessageSquare,
  FiClock,
  FiUser,
  FiFileText,
  FiAlertCircle,
  FiCheckCircle,
  FiX,
  FiRefreshCw,
  FiFilter,
  FiPlus,
  FiEdit3,
  FiTrash2,
  FiEye,
  FiSend,
  FiUserCheck,
  FiUserX,
  FiMail,
  FiPhone,
  FiMessageCircle,
  FiSettings,
  FiBarChart3
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { SidebarProvider, useSidebar } from '../utils/SidebarContext';
import axiosInstance from '../services/axiosInstance';
import AuthContext from '../services/AuthContext';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const AdminReclamationsManager = () => {
  const { isOpen } = useSidebar();
  const { user } = useContext(AuthContext);
  const toast = useToast();
  const contentMarginRight = useBreakpointValue({ base: '0px', md: isOpen ? '280px' : '80px' });

  // Platform colors
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';
  const bgColor = useColorModeValue('#f8f9fa', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('#2C3E50', 'white');

  // Helper functions
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'yellow';
      case 'in_progress': return 'blue';
      case 'resolved': return 'green';
      case 'closed': return 'gray';
      case 'rejected': return 'red';
      default: return 'yellow';
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'قيد الانتظار';
      case 'in_progress': return 'قيد المعالجة';
      case 'resolved': return 'تم الحل';
      case 'closed': return 'مغلقة';
      case 'rejected': return 'مرفوضة';
      default: return 'قيد الانتظار';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'low': return 'green';
      case 'medium': return 'yellow';
      case 'high': return 'orange';
      case 'urgent': return 'red';
      default: return 'blue';
    }
  };

  const getPriorityText = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'low': return 'منخفضة';
      case 'medium': return 'متوسطة';
      case 'high': return 'عالية';
      case 'urgent': return 'عاجلة';
      default: return 'متوسطة';
    }
  };

  const getCategoryText = (category) => {
    const categoryMap = {
      "technical": "مشاكل تقنية",
      "content": "مشاكل في المحتوى",
      "payment": "مشاكل في الدفع",
      "instructor": "مشاكل مع المدرس",
      "course": "مشاكل في الدورة",
      "account": "مشاكل في الحساب",
      "suggestion": "اقتراح",
      "other": "أخرى"
    };
    return categoryMap[category] || category;
  };

  const getContactMethodText = (method) => {
    const methodMap = {
      "email": "بريد إلكتروني",
      "phone": "هاتف",
      "whatsapp": "واتساب",
      "platform": "المنصة"
    };
    return methodMap[method] || method;
  };

  // State
  const [reclamations, setReclamations] = useState([]);
  const [filteredReclamations, setFilteredReclamations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterAssignedTo, setFilterAssignedTo] = useState('all');
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'

  // Available admins/staff for assignment
  const [availableAdmins, setAvailableAdmins] = useState([]);

  // Modal states
  const { isOpen: isViewOpen, onOpen: onViewOpen, onClose: onViewClose } = useDisclosure();
  const { isOpen: isEditOpen, onOpen: onEditOpen, onClose: onEditClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isAddNoteOpen, onOpen: onAddNoteOpen, onClose: onAddNoteClose } = useDisclosure();
  const { isOpen: isAddCommunicationOpen, onOpen: onAddCommunicationOpen, onClose: onAddCommunicationClose } = useDisclosure();

  const [selectedReclamation, setSelectedReclamation] = useState(null);
  const [editFormData, setEditFormData] = useState({
    status: '',
    assignedTo: '',
    priority: '',
    adminResponse: '',
    resolutionDetails: ''
  });
  const [newNote, setNewNote] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(true);
  const [newCommunication, setNewCommunication] = useState({
    type: 'platform_message',
    direction: 'outgoing',
    content: ''
  });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    inProgress: 0,
    resolved: 0,
    closed: 0,
    urgent: 0,
    assigned: 0,
    unassigned: 0
  });

  useEffect(() => {
    if (user?.role == "admin") {
      fetchReclamations();
      fetchReclamationStats();
      fetchAvailableAdmins();
    } else {
      setIsLoading(false);
      toast({
        title: 'غير مصرح لك',
        description: 'ليس لديك صلاحية الوصول إلى هذه الصفحة.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [user]);

  useEffect(() => {
    filterReclamations();
  }, [reclamations, searchTerm, filterStatus, filterPriority, filterCategory, filterAssignedTo]);

  const fetchReclamations = async () => {
    try {
      setIsLoading(true);
      const response = await axiosInstance.get('/reclamations/');
      const reclamationData = response.data?.reclamations || response.data || [];
      setReclamations(Array.isArray(reclamationData) ? reclamationData : []);
    } catch (error) {
      console.error('خطأ في جلب الشكاوى:', error);
      toast({
        title: 'خطأ في تحميل البيانات',
        description: 'فشل في تحميل قائمة الشكاوى.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setReclamations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReclamationStats = async () => {
    try {
      const response = await axiosInstance.get('/reclamations/stats/admin');
      setStats(response.data || {});
    } catch (error) {
      console.error('خطأ في جلب الإحصائيات:', error);
      // Calculate stats locally if API fails
      calculateLocalStats();
    }
  };

  const fetchAvailableAdmins = async () => {
    try {
      // This would typically be an endpoint to get admin/staff users
      // For now, we'll use a placeholder
      const response = await axiosInstance.get('/admin/staff');
      setAvailableAdmins(response.data || []);
    } catch (error) {
      console.error('خطأ في جلب قائمة الإداريين:', error);
      // Set placeholder admins if API fails
      setAvailableAdmins([
        { _id: 'admin1', username: 'المسؤول الأول' },
        { _id: 'admin2', username: 'المسؤول الثاني' }
      ]);
    }
  };

  const calculateLocalStats = () => {
    const total = reclamations.length;
    const pending = reclamations.filter(r => r.status === 'pending').length;
    const inProgress = reclamations.filter(r => r.status === 'in_progress').length;
    const resolved = reclamations.filter(r => r.status === 'resolved').length;
    const closed = reclamations.filter(r => r.status === 'closed').length;
    const urgent = reclamations.filter(r => r.priority === 'urgent' || r.isUrgent).length;
    const assigned = reclamations.filter(r => r.assignedTo).length;
    const unassigned = reclamations.filter(r => !r.assignedTo).length;

    setStats({
      total,
      pending,
      inProgress,
      resolved,
      closed,
      urgent,
      assigned,
      unassigned
    });
  };

  const filterReclamations = () => {
    let filtered = Array.isArray(reclamations) ? [...reclamations] : [];

    if (searchTerm) {
      filtered = filtered.filter(reclamation =>
        reclamation.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reclamation.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reclamation.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reclamation.user?.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        reclamation.user?.email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(reclamation => reclamation.status === filterStatus);
    }

    if (filterPriority !== 'all') {
      filtered = filtered.filter(reclamation => reclamation.priority === filterPriority);
    }

    if (filterCategory !== 'all') {
      filtered = filtered.filter(reclamation => reclamation.category === filterCategory);
    }

    if (filterAssignedTo !== 'all') {
      if (filterAssignedTo === 'unassigned') {
        filtered = filtered.filter(reclamation => !reclamation.assignedTo);
      } else {
        filtered = filtered.filter(reclamation => reclamation.assignedTo?._id === filterAssignedTo);
      }
    }

    // Sort by priority and creation date
    filtered.sort((a, b) => {
      const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
      const aPriority = priorityOrder[a.priority] || 2;
      const bPriority = priorityOrder[b.priority] || 2;

      if (aPriority !== bPriority) {
        return bPriority - aPriority;
      }

      return new Date(b.createdAt) - new Date(a.createdAt);
    });

    setFilteredReclamations(filtered);
  };

  const handleViewReclamation = (reclamation) => {
    setSelectedReclamation(reclamation);
    onViewOpen();
  };

  const handleEditReclamation = (reclamation) => {
    setSelectedReclamation(reclamation);
    setEditFormData({
      status: reclamation.status || '',
      assignedTo: reclamation.assignedTo?._id || '',
      priority: reclamation.priority || 'medium',
      adminResponse: reclamation.adminResponse || '',
      resolutionDetails: reclamation.resolutionDetails || ''
    });
    onEditOpen();
  };

  const handleUpdateReclamation = async () => {
    try {
      const updates = [];

      // Update status if changed
      if (editFormData.status !== selectedReclamation.status) {
        updates.push(
          axiosInstance.patch(`/reclamations/${selectedReclamation._id}/status`, {
            status: editFormData.status,
            ...(editFormData.resolutionDetails && { resolutionDetails: editFormData.resolutionDetails })
          })
        );
      }

      // Update assignment if changed


      // Execute all updates
      await Promise.all(updates);

      toast({
        title: 'تم تحديث الشكوى',
        description: 'تم تحديث تفاصيل الشكوى بنجاح.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onEditClose();
      fetchReclamations();
      fetchReclamationStats();
    } catch (error) {
      console.error('خطأ في تحديث الشكوى:', error);
      toast({
        title: 'خطأ في التحديث',
        description: error.response?.data?.message || 'فشل في تحديث الشكوى، يرجى المحاولة مرة أخرى.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteReclamation = (reclamation) => {
    setSelectedReclamation(reclamation);
    onDeleteOpen();
  };

  const confirmDelete = async () => {
    try {
      await axiosInstance.delete(`/reclamations/${selectedReclamation._id}`);

      toast({
        title: 'تم حذف الشكوى',
        description: 'تم حذف الشكوى بنجاح.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      onDeleteClose();
      fetchReclamations();
      fetchReclamationStats();
    } catch (error) {
      console.error('خطأ في حذف الشكوى:', error);
      toast({
        title: 'خطأ في الحذف',
        description: error.response?.data?.message || 'فشل في حذف الشكوى، يرجى المحاولة مرة أخرى.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast({
        title: 'خطأ في الإدخال',
        description: 'يرجى إدخال نص الملاحظة.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await axiosInstance.post(`/reclamations/${selectedReclamation._id}/notes`, {
        note: newNote,
        isInternal: isInternalNote
      });

      toast({
        title: 'تم إضافة الملاحظة',
        description: 'تم إضافة الملاحظة الإدارية بنجاح.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setNewNote('');
      setIsInternalNote(true);
      onAddNoteClose();
      fetchReclamations();
    } catch (error) {
      console.error('خطأ في إضافة الملاحظة:', error);
      toast({
        title: 'خطأ في الإضافة',
        description: error.response?.data?.message || 'فشل في إضافة الملاحظة، يرجى المحاولة مرة أخرى.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleAddCommunication = async () => {
    if (!newCommunication.content.trim()) {
      toast({
        title: 'خطأ في الإدخال',
        description: 'يرجى إدخال محتوى التواصل.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      await axiosInstance.post(`/reclamations/${selectedReclamation._id}/communications`, newCommunication);

      toast({
        title: 'تم إضافة التواصل',
        description: 'تم إضافة سجل التواصل بنجاح.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      setNewCommunication({ type: 'platform_message', direction: 'outgoing', content: '' });
      onAddCommunicationClose();
      fetchReclamations();
    } catch (error) {
      console.error('خطأ في إضافة التواصل:', error);
      toast({
        title: 'خطأ في الإضافة',
        description: error.response?.data?.message || 'فشل في إضافة سجل التواصل، يرجى المحاولة مرة أخرى.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleQuickStatusUpdate = async (reclamationId, newStatus) => {
    try {
      await axiosInstance.patch(`/reclamations/${reclamationId}/status`, { status: newStatus });

      toast({
        title: 'تم تحديث الحالة',
        description: `تم تحديث حالة الشكوى إلى "${getStatusText(newStatus)}"`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchReclamations();
      fetchReclamationStats();
    } catch (error) {
      console.error('خطأ في تحديث الحالة:', error);
      toast({
        title: 'خطأ في التحديث',
        description: 'فشل في تحديث حالة الشكوى.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleQuickAssignment = async (reclamationId, adminId) => {
    try {
      await axiosInstance.patch(`/reclamations/${reclamationId}/assign`, { assignedTo: adminId });

      const admin = availableAdmins.find(a => a._id === adminId);
      toast({
        title: 'تم التعيين',
        description: `تم تعيين الشكوى إلى ${admin?.username || 'المسؤول'}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchReclamations();
      fetchReclamationStats();
    } catch (error) {
      console.error('خطأ في التعيين:', error);
      toast({
        title: 'خطأ في التعيين',
        description: 'فشل في تعيين الشكوى.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  if (isLoading) {
    return (
      <Flex h="100vh" align="center" justify="center" dir="rtl">
        <VStack spacing={4}>
          <CobraSpinner size="xl" color={primaryColor} thickness="4px" />
          <Text color={textColor} fontFamily="Tajawal, sans-serif">جاري تحميل الشكاوى...</Text>
        </VStack>
      </Flex>
    );
  }

  if (!user?.role == "admin") {
    return (
      <Flex h="100vh" align="center" justify="center" dir="rtl">
        <Alert status='error' variant='subtle' flexDirection='column' alignItems='center' justifyContent='center' textAlign='center' height='200px'>
          <AlertIcon boxSize='40px' mr={0} />
          <AlertTitle mt={4} mb={1} fontSize='lg'>غير مصرح لك!</AlertTitle>
          <AlertDescription maxWidth='sm'>ليس لديك صلاحية الوصول إلى هذه الصفحة.</AlertDescription>
        </Alert>
      </Flex>
    );
  }

  return (

    <Box dir="rtl" fontFamily="Tajawal, sans-serif">
      <Flex h="100vh" overflowX="hidden" flexDirection="column">
        <Box
          flex="1"
          p={6}
          bg={bgColor}
          transition="margin-right 0.3s ease"
          overflowY="auto"
        >
          {/* Header */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" mb={6}>
              <CardBody p={6}>
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  flexDirection={{ base: 'column', md: 'row' }}
                  gap={4}
                >
                  <VStack align="start" spacing={2}>
                    <Heading
                      as="h1"
                      size="xl"
                      color={secondaryColor}
                      fontFamily="Tajawal, sans-serif"
                      fontWeight="700"
                    >
                      إدارة الشكاوى والاقتراحات 📝
                    </Heading>
                    <Text color="gray.600" fontSize="md">
                      عرض وإدارة جميع الشكاوى والاقتراحات المقدمة من المستخدمين.
                    </Text>
                  </VStack>

                  <HStack spacing={3}>
                    <Button
                      variant="outline"
                      borderColor="gray.300"
                      _hover={{ borderColor: primaryColor, color: primaryColor }}
                      leftIcon={<FiRefreshCw />}
                      onClick={fetchReclamations}
                      fontFamily="Tajawal, sans-serif"
                    >
                      تحديث
                    </Button>
                    <Button
                      variant={viewMode === 'cards' ? 'solid' : 'outline'}
                      bg={viewMode === 'cards' ? primaryColor : 'transparent'}
                      color={viewMode === 'cards' ? secondaryColor : 'gray.600'}
                      borderColor="gray.300"
                      _hover={{ borderColor: primaryColor, color: primaryColor }}
                      onClick={() => setViewMode('cards')}
                      fontFamily="Tajawal, sans-serif"
                    >
                      بطاقات
                    </Button>
                    <Button
                      variant={viewMode === 'table' ? 'solid' : 'outline'}
                      bg={viewMode === 'table' ? primaryColor : 'transparent'}
                      color={viewMode === 'table' ? secondaryColor : 'gray.600'}
                      borderColor="gray.300"
                      _hover={{ borderColor: primaryColor, color: primaryColor }}
                      onClick={() => setViewMode('table')}
                      fontFamily="Tajawal, sans-serif"
                    >
                      جدول
                    </Button>
                  </HStack>
                </Flex>
              </CardBody>
            </Card>
          </motion.div>

          {/* Stats Grid */}


          {/* Filters and Search */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200" mb={6}>
              <CardBody p={6}>
                <VStack spacing={4} align="stretch">
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.700" />
                    </InputLeftElement>
                    <Input
                      placeholder="بحث بالعنوان أو الوصف أو الفئة أو اسم المستخدم..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      fontFamily="Tajawal, sans-serif"
                    />
                  </InputGroup>

                  <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                    <Select
                      placeholder="تصفية حسب الحالة"
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      fontFamily="Tajawal, sans-serif"
                    >
                      <option value="all">جميع الحالات</option>
                      <option value="pending">قيد الانتظار</option>
                      <option value="in_progress">قيد المعالجة</option>
                      <option value="resolved">تم الحل</option>
                      <option value="closed">مغلقة</option>
                      <option value="rejected">مرفوضة</option>
                    </Select>
                    <Select
                      placeholder="تصفية حسب الأولوية"
                      value={filterPriority}
                      onChange={(e) => setFilterPriority(e.target.value)}
                      fontFamily="Tajawal, sans-serif"
                    >
                      <option value="all">جميع الأولويات</option>
                      <option value="low">منخفضة</option>
                      <option value="medium">متوسطة</option>
                      <option value="high">عالية</option>
                      <option value="urgent">عاجلة</option>
                    </Select>
                    <Select
                      placeholder="تصفية حسب الفئة"
                      value={filterCategory}
                      onChange={(e) => setFilterCategory(e.target.value)}
                      fontFamily="Tajawal, sans-serif"
                    >
                      <option value="all">جميع الفئات</option>
                      <option value="technical">مشاكل تقنية</option>
                      <option value="content">مشاكل في المحتوى</option>
                      <option value="payment">مشاكل في الدفع</option>
                      <option value="instructor">مشاكل مع المدرس</option>
                      <option value="course">مشاكل في الدورة</option>
                      <option value="account">مشاكل في الحساب</option>
                      <option value="suggestion">اقتراح</option>
                      <option value="other">أخرى</option>
                    </Select>
                    <Select
                      placeholder="تصفية حسب المسؤول"
                      value={filterAssignedTo}
                      onChange={(e) => setFilterAssignedTo(e.target.value)}
                      fontFamily="Tajawal, sans-serif"
                    >
                      <option value="all">الكل</option>
                      <option value="unassigned">غير معين</option>
                      {availableAdmins.map(admin => (
                        <option key={admin._id} value={admin._id}>{admin.username}</option>
                      ))}
                    </Select>
                  </SimpleGrid>
                </VStack>
              </CardBody>
            </Card>
          </motion.div>

          {/* Reclamations Content */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            {filteredReclamations.length === 0 && !isLoading ? (
              <Alert status='info' variant='left-accent' flexDirection='column' alignItems='center' justifyContent='center' textAlign='center' height='200px'>
                <AlertIcon boxSize='40px' mr={0} />
                <AlertTitle mt={4} mb={1} fontSize='lg'>لا توجد شكاوى مطابقة</AlertTitle>
                <AlertDescription maxWidth='sm'>لا توجد شكاوى تتوافق مع معايير البحث أو التصفية الحالية.</AlertDescription>
              </Alert>
            ) : viewMode === 'cards' ? (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                {filteredReclamations.map(reclamation => (
                  <ReclamationCard
                    key={reclamation._id}
                    reclamation={reclamation}
                    onView={handleViewReclamation}
                    onEdit={handleEditReclamation}
                    onDelete={handleDeleteReclamation}
                    onQuickStatusUpdate={handleQuickStatusUpdate}
                    onQuickAssignment={handleQuickAssignment}
                    availableAdmins={availableAdmins}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    textColor={textColor}
                    cardBg={cardBg}
                    user={user}
                    getStatusColor={getStatusColor}
                    getStatusText={getStatusText}
                    getPriorityColor={getPriorityColor}
                    getPriorityText={getPriorityText}
                    getCategoryText={getCategoryText}
                  />
                ))}
              </SimpleGrid>
            ) : (
              <ReclamationsTable
                reclamations={filteredReclamations}
                onView={handleViewReclamation}
                onEdit={handleEditReclamation}
                onDelete={handleDeleteReclamation}
                onQuickStatusUpdate={handleQuickStatusUpdate}
                onQuickAssignment={handleQuickAssignment}
                availableAdmins={availableAdmins}
                getStatusColor={getStatusColor}
                getStatusText={getStatusText}
                getPriorityColor={getPriorityColor}
                getPriorityText={getPriorityText}
                getCategoryText={getCategoryText}
              />
            )}
          </motion.div>
        </Box>
      </Flex>

      {/* Modals */}
      <ReclamationViewModal
        isOpen={isViewOpen}
        onClose={onViewClose}
        reclamation={selectedReclamation}
        onAddNote={() => {
          onViewClose();
          onAddNoteOpen();
        }}
        onAddCommunication={() => {
          onViewClose();
          onAddCommunicationOpen();
        }}
        getStatusColor={getStatusColor}
        getStatusText={getStatusText}
        getPriorityColor={getPriorityColor}
        getPriorityText={getPriorityText}
        getCategoryText={getCategoryText}
        getContactMethodText={getContactMethodText}
        secondaryColor={secondaryColor}
      />

      <ReclamationEditModal
        isOpen={isEditOpen}
        onClose={onEditClose}
        reclamation={selectedReclamation}
        formData={editFormData}
        setFormData={setEditFormData}
        onUpdate={handleUpdateReclamation}
        availableAdmins={availableAdmins}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />

      <ReclamationDeleteModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        reclamation={selectedReclamation}
        onConfirm={confirmDelete}
      />

      <AddNoteModal
        isOpen={isAddNoteOpen}
        onClose={onAddNoteClose}
        note={newNote}
        setNote={setNewNote}
        isInternal={isInternalNote}
        setIsInternal={setIsInternalNote}
        onAdd={handleAddNote}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />

      <AddCommunicationModal
        isOpen={isAddCommunicationOpen}
        onClose={onAddCommunicationClose}
        communication={newCommunication}
        setCommunication={setNewCommunication}
        onAdd={handleAddCommunication}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
      />
    </Box>
  );
};

// Component: ReclamationCard
const ReclamationCard = ({
  reclamation,
  onView,
  onEdit,
  onDelete,
  onQuickStatusUpdate,
  onQuickAssignment,
  availableAdmins,
  primaryColor,
  secondaryColor,
  textColor,
  cardBg,
  user,
  getStatusColor,
  getStatusText,
  getPriorityColor,
  getPriorityText,
  getCategoryText
}) => {
  return (
    <motion.div variants={fadeIn}>
      <Card
        bg={cardBg}
        borderRadius="xl"
        boxShadow="md"
        border="1px solid"
        borderColor="gray.200"
        _hover={{
          transform: "translateY(-2px)",
          boxShadow: "lg",
          borderColor: primaryColor
        }}
        transition="all 0.3s ease"
        overflow="hidden"
      >
        <CardHeader pb={2}>
          <Flex justify="space-between" align="flex-start">
            <VStack align="flex-start" spacing={1} flex="1">
              <Heading size="md" color={secondaryColor} fontFamily="Tajawal, sans-serif">
                {reclamation.title || 'شكوى بدون عنوان'}
              </Heading>
              <Text fontSize="sm" color="gray.600" noOfLines={2}>
                {reclamation.description || 'لا يوجد وصف متاح'}
              </Text>
            </VStack>
            <VStack spacing={2} align="flex-end">
              <Badge
                colorScheme={getStatusColor(reclamation.status)}
                variant="subtle"
                borderRadius="full"
                px={3}
                py={1}
              >
                {getStatusText(reclamation.status)}
              </Badge>
              <Badge
                colorScheme={getPriorityColor(reclamation.priority)}
                variant="outline"
                borderRadius="full"
                px={2}
                py={1}
                fontSize="xs"
              >
                {getPriorityText(reclamation.priority)}
              </Badge>
              {reclamation.isUrgent && (
                <Badge colorScheme="red" variant="solid" borderRadius="full" px={2} py={1} fontSize="xs">
                  عاجل
                </Badge>
              )}
            </VStack>
          </Flex>
        </CardHeader>

        <CardBody pt={0}>
          <VStack spacing={4} align="stretch">
            {/* Reclamation Info */}
            <VStack spacing={2} align="stretch">
              <HStack justify="space-between">
                <HStack>
                  <Icon as={FiUser} color="gray.500" />
                  <Text fontSize="sm" color="gray.600">
                    {reclamation.user?.name || 'غير محدد'}
                  </Text>
                </HStack>
                <HStack>
                  <Icon as={FiClock} color="gray.500" />
                  <Text fontSize="sm" color="gray.600">
                    {reclamation.createdAt ?
                      format(new Date(reclamation.createdAt), 'dd/MM/yyyy', { locale: ar }) :
                      'غير محدد'}
                  </Text>
                </HStack>
              </HStack>

              {reclamation.category && (
                <HStack>
                  <Icon as={FiFileText} color="gray.500" />
                  <Text fontSize="sm" color="gray.600">
                    الفئة: {getCategoryText(reclamation.category)}
                  </Text>
                </HStack>
              )}

              {reclamation.assignedTo && (
                <HStack>
                  <Icon as={FiUserCheck} color="gray.500" />
                  <Text fontSize="sm" color="gray.600">
                    المسؤول: {reclamation.assignedTo.username}
                  </Text>
                </HStack>
              )}

              {reclamation.user?.email && (
                <HStack>
                  <Icon as={FiMail} color="gray.500" />
                  <Text fontSize="sm" color="gray.600">
                    {reclamation.user.email}
                  </Text>
                </HStack>
              )}

              {reclamation.adminResponse && (
                <Box
                  bg="blue.50"
                  p={3}
                  borderRadius="md"
                  border="1px solid"
                  borderColor="blue.200"
                >
                  <Text fontSize="sm" color="blue.700" fontWeight="medium">
                    الرد: {reclamation.adminResponse}
                  </Text>
                </Box>
              )}
            </VStack>

            <Divider />

            {/* Quick Actions */}
            <VStack spacing={2} align="stretch">
              <HStack spacing={2}>
                <Menu>
                  <MenuButton as={Button} size="sm" variant="outline" rightIcon={<ChevronDownIcon />}>
                    تغيير الحالة
                  </MenuButton>
                  <MenuList>
                    <MenuItem onClick={() => onQuickStatusUpdate(reclamation._id, 'pending')}>
                      قيد الانتظار
                    </MenuItem>
                    <MenuItem onClick={() => onQuickStatusUpdate(reclamation._id, 'in_progress')}>
                      قيد المعالجة
                    </MenuItem>
                    <MenuItem onClick={() => onQuickStatusUpdate(reclamation._id, 'resolved')}>
                      تم الحل
                    </MenuItem>
                    <MenuItem onClick={() => onQuickStatusUpdate(reclamation._id, 'closed')}>
                      مغلقة
                    </MenuItem>
                    <MenuItem onClick={() => onQuickStatusUpdate(reclamation._id, 'rejected')}>
                      مرفوضة
                    </MenuItem>
                  </MenuList>
                </Menu>


              </HStack>

              {/* Action Buttons */}
              <HStack spacing={2}>
                <Button
                  flex="1"
                  variant="outline"
                  borderColor="gray.300"
                  _hover={{ borderColor: primaryColor, color: primaryColor }}
                  leftIcon={<FiEye />}
                  onClick={() => onView(reclamation)}
                  fontFamily="Tajawal, sans-serif"
                  size="sm"
                >
                  عرض
                </Button>

                <IconButton
                  aria-label="تعديل"
                  icon={<FiEdit3 />}
                  variant="outline"
                  borderColor="blue.300"
                  color="blue.500"
                  _hover={{ borderColor: "blue.500", bg: "blue.50" }}
                  onClick={() => onEdit(reclamation)}
                  size="sm"
                />

                <IconButton
                  aria-label="حذف"
                  icon={<FiTrash2 />}
                  variant="outline"
                  borderColor="red.300"
                  color="red.500"
                  _hover={{ borderColor: "red.500", bg: "red.50" }}
                  onClick={() => onDelete(reclamation)}
                  size="sm"
                />
              </HStack>
            </VStack>
          </VStack>
        </CardBody>
      </Card>
    </motion.div>
  );
};

// Component: ReclamationsTable
const ReclamationsTable = ({
  reclamations,
  onView,
  onEdit,
  onDelete,
  onQuickStatusUpdate,
  onQuickAssignment,
  availableAdmins,
  getStatusColor,
  getStatusText,
  getPriorityColor,
  getPriorityText,
  getCategoryText
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');

  return (
    <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
      <CardBody p={0}>
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th textAlign="right">العنوان</Th>
                <Th textAlign="right">المستخدم</Th>
                <Th textAlign="right">الفئة</Th>
                <Th textAlign="right">الحالة</Th>
                <Th textAlign="right">الأولوية</Th>

                <Th textAlign="right">التاريخ</Th>
                <Th textAlign="right">الإجراءات</Th>
              </Tr>
            </Thead>
            <Tbody>
              {reclamations.map(reclamation => (
                <Tr key={reclamation._id} _hover={{ bg: 'gray.50' }}>
                  <Td>
                    <VStack align="start" spacing={1}>
                      <Text fontWeight="medium" noOfLines={1}>
                        {reclamation.title}
                      </Text>
                      <Text fontSize="xs" color="gray.500" noOfLines={1}>
                        {reclamation.description}
                      </Text>
                    </VStack>
                  </Td>
                  <Td>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm">{reclamation.user?.username}</Text>
                      <Text fontSize="xs" color="gray.500">{reclamation.user?.email}</Text>
                    </VStack>
                  </Td>
                  <Td>
                    <Text fontSize="sm">{getCategoryText(reclamation.category)}</Text>
                  </Td>
                  <Td>
                    <Badge colorScheme={getStatusColor(reclamation.status)} variant="subtle">
                      {getStatusText(reclamation.status)}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={getPriorityColor(reclamation.priority)} variant="outline">
                      {getPriorityText(reclamation.priority)}
                    </Badge>
                  </Td>

                  <Td>
                    <Text fontSize="sm">
                      {format(new Date(reclamation.createdAt), 'dd/MM/yyyy', { locale: ar })}
                    </Text>
                  </Td>
                  <Td>
                    <HStack spacing={1}>
                      <IconButton
                        aria-label="عرض"
                        icon={<FiEye />}
                        size="xs"
                        variant="ghost"
                        onClick={() => onView(reclamation)}
                      />
                      <IconButton
                        aria-label="تعديل"
                        icon={<FiEdit3 />}
                        size="xs"
                        variant="ghost"
                        colorScheme="blue"
                        onClick={() => onEdit(reclamation)}
                      />
                      <IconButton
                        aria-label="حذف"
                        icon={<FiTrash2 />}
                        size="xs"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => onDelete(reclamation)}
                      />
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </CardBody>
    </Card>
  );
};

// Component: StatCard
const StatCard = ({ title, value, icon, color }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('#2C3E50', 'white');

  return (
    <motion.div variants={fadeIn}>
      <Card bg={cardBg} borderRadius="xl" boxShadow="md" border="1px solid" borderColor="gray.200">
        <CardBody>
          <Flex alignItems="center" justifyContent="space-between">
            <Stat>
              <StatLabel fontSize="md" color="gray.600" fontFamily="Tajawal, sans-serif">{title}</StatLabel>
              <StatNumber fontSize="3xl" fontWeight="bold" color={textColor} fontFamily="Tajawal, sans-serif">{value}</StatNumber>
            </Stat>
            <Icon as={icon} boxSize={12} color={color} />
          </Flex>
        </CardBody>
      </Card>
    </motion.div>
  );
};

// Component: ReclamationViewModal
const ReclamationViewModal = ({
  isOpen,
  onClose,
  reclamation,
  onAddNote,
  onAddCommunication,
  getStatusColor,
  getStatusText,
  getPriorityColor,
  getPriorityText,
  getCategoryText,
  getContactMethodText,
  secondaryColor
}) => {
  if (!reclamation) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent dir="rtl" fontFamily="Tajawal, sans-serif" maxH="90vh" overflowY="auto">
        <ModalHeader color={secondaryColor}>تفاصيل الشكوى</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <SimpleGrid columns={2} spacing={4}>
              <Box>
                <Text fontWeight="bold" mb={1}>العنوان:</Text>
                <Text>{reclamation.title}</Text>
              </Box>
              <Box>
                <Text fontWeight="bold" mb={1}>الحالة:</Text>
                <Badge colorScheme={getStatusColor(reclamation.status)}>
                  {getStatusText(reclamation.status)}
                </Badge>
              </Box>
            </SimpleGrid>

            <Box>
              <Text fontWeight="bold" mb={1}>الوصف:</Text>
              <Text>{reclamation.description}</Text>
            </Box>

            <SimpleGrid columns={2} spacing={4}>
              <Box>
                <Text fontWeight="bold" mb={1}>الأولوية:</Text>
                <Badge colorScheme={getPriorityColor(reclamation.priority)}>
                  {getPriorityText(reclamation.priority)}
                </Badge>
              </Box>
              <Box>
                <Text fontWeight="bold" mb={1}>الفئة:</Text>
                <Text>{getCategoryText(reclamation.category)}</Text>
              </Box>
            </SimpleGrid>

            <SimpleGrid columns={2} spacing={4}>
              <Box>
                <Text fontWeight="bold" mb={1}>المستخدم:</Text>
                <Text>{reclamation.user?.username || 'غير محدد'}</Text>
                {reclamation.user?.email && (
                  <Text fontSize="sm" color="gray.500">{reclamation.user.email}</Text>
                )}
              </Box>
              <Box>
                <Text fontWeight="bold" mb={1}>تاريخ الإنشاء:</Text>
                <Text>{format(new Date(reclamation.createdAt), 'dd/MM/yyyy HH:mm', { locale: ar })}</Text>
              </Box>
            </SimpleGrid>

            {reclamation.assignedTo && (
              <Box>
                <Text fontWeight="bold" mb={1}>المسؤول:</Text>
                <Text>{reclamation.assignedTo.username}</Text>
                {reclamation.assignedAt && (
                  <Text fontSize="sm" color="gray.500">
                    تم التعيين في: {format(new Date(reclamation.assignedAt), 'dd/MM/yyyy HH:mm', { locale: ar })}
                  </Text>
                )}
              </Box>
            )}

            {(reclamation.contactEmail || reclamation.contactPhone) && (
              <Box>
                <Text fontWeight="bold" mb={1}>معلومات التواصل:</Text>
                {reclamation.contactEmail && (
                  <Text fontSize="sm">البريد الإلكتروني: {reclamation.contactEmail}</Text>
                )}
                {reclamation.contactPhone && (
                  <Text fontSize="sm">الهاتف: {reclamation.contactPhone}</Text>
                )}
                {reclamation.preferredContactMethod && (
                  <Text fontSize="sm">الطريقة المفضلة: {getContactMethodText(reclamation.preferredContactMethod)}</Text>
                )}
              </Box>
            )}

            {(reclamation.relatedCourse || reclamation.relatedInstructor) && (
              <Box>
                <Text fontWeight="bold" mb={1}>معلومات ذات صلة:</Text>
                {reclamation.relatedCourse && (
                  <Text fontSize="sm">الدورة: {reclamation.relatedCourse.title}</Text>
                )}
                {reclamation.relatedInstructor && (
                  <Text fontSize="sm">المدرس: {reclamation.relatedInstructor.username}</Text>
                )}
              </Box>
            )}

            {reclamation.resolutionDetails && (
              <Box mt={4} p={3} bg="green.50" borderRadius="md">
                <Text fontWeight="bold" color="green.700" mb={1}>تفاصيل الحل:</Text>
                <Text color="green.700">{reclamation.resolutionDetails}</Text>
                {reclamation.resolvedAt && (
                  <Text fontSize="sm" color="green.600" mt={1}>
                    تم الحل في: {format(new Date(reclamation.resolvedAt), 'dd/MM/yyyy HH:mm', { locale: ar })}
                  </Text>
                )}
              </Box>
            )}

            <Divider />

            <Box>
              <Flex justify="space-between" align="center" mb={3}>
                <Heading size="sm" color={secondaryColor}>ملاحظات الإدارة</Heading>
                <Button size="sm" leftIcon={<FiPlus />} onClick={onAddNote}>
                  إضافة ملاحظة
                </Button>
              </Flex>
              {reclamation.adminNotes && reclamation.adminNotes.length > 0 ? (
                <VStack align="stretch" spacing={2}>
                  {reclamation.adminNotes.map((note, index) => (
                    <Box key={index} p={3} bg={note.isInternal ? "orange.50" : "blue.50"} borderRadius="md">
                      <Text fontSize="sm">{note.note}</Text>
                      <HStack justify="space-between" mt={1}>
                        <Text fontSize="xs" color="gray.500">
                          بواسطة: {note.addedBy?.username || 'غير محدد'}
                        </Text>
                        <HStack>
                          {note.isInternal && (
                            <Badge size="sm" colorScheme="orange">داخلية</Badge>
                          )}
                          <Text fontSize="xs" color="gray.500">
                            {format(new Date(note.addedAt), 'dd/MM/yyyy HH:mm', { locale: ar })}
                          </Text>
                        </HStack>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              ) : (
                <Text fontSize="sm" color="gray.500">لا توجد ملاحظات إدارية.</Text>
              )}
            </Box>

            <Divider />

            <Box>
              <Flex justify="space-between" align="center" mb={3}>
                <Heading size="sm" color={secondaryColor}>سجل التواصل</Heading>
                <Button size="sm" leftIcon={<FiSend />} onClick={onAddCommunication}>
                  إضافة تواصل
                </Button>
              </Flex>
              {reclamation.communications && reclamation.communications.length > 0 ? (
                <VStack align="stretch" spacing={2}>
                  {reclamation.communications.map((comm, index) => (
                    <Box
                      key={index}
                      p={3}
                      bg={comm.direction === 'incoming' ? 'blue.50' : 'purple.50'}
                      borderRadius="md"
                    >
                      <Text fontSize="sm">{comm.content}</Text>
                      <HStack justify="space-between" mt={1}>
                        <HStack>
                          <Badge size="sm" colorScheme={comm.direction === 'incoming' ? 'blue' : 'purple'}>
                            {comm.direction === 'incoming' ? 'وارد' : 'صادر'}
                          </Badge>
                          <Badge size="sm" variant="outline">
                            {comm.type}
                          </Badge>
                        </HStack>
                        <Text fontSize="xs" color="gray.500">
                          {comm.sentBy?.username || 'غير محدد'} - {format(new Date(comm.sentAt), 'dd/MM/yyyy HH:mm', { locale: ar })}
                        </Text>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              ) : (
                <Text fontSize="sm" color="gray.500">لا يوجد سجل تواصل.</Text>
              )}
            </Box>

            {reclamation.userSatisfaction && (
              <Box mt={4} p={3} bg="yellow.50" borderRadius="md">
                <Text fontWeight="bold" color="yellow.700" mb={1}>تقييم المستخدم:</Text>
                <Text color="yellow.700">التقييم: {reclamation.userSatisfaction.rating}/5</Text>
                {reclamation.userSatisfaction.feedback && (
                  <Text color="yellow.700" mt={1}>التعليق: {reclamation.userSatisfaction.feedback}</Text>
                )}
              </Box>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose}>إغلاق</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Component: ReclamationEditModal
const ReclamationEditModal = ({
  isOpen,
  onClose,
  reclamation,
  formData,
  setFormData,
  onUpdate,
  availableAdmins,
  primaryColor,
  secondaryColor
}) => {
  if (!reclamation) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent dir="rtl" fontFamily="Tajawal, sans-serif">
        <ModalHeader color={secondaryColor}>تعديل الشكوى</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl>
              <FormLabel>الحالة</FormLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="pending">قيد الانتظار</option>
                <option value="in_progress">قيد المعالجة</option>
                <option value="resolved">تم الحل</option>
                <option value="closed">مغلقة</option>
                <option value="rejected">مرفوضة</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>الأولوية</FormLabel>
              <Select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              >
                <option value="low">منخفضة</option>
                <option value="medium">متوسطة</option>
                <option value="high">عالية</option>
                <option value="urgent">عاجلة</option>
              </Select>
            </FormControl>



            {formData.status === 'resolved' && (
              <FormControl>
                <FormLabel>تفاصيل الحل</FormLabel>
                <Textarea
                  value={formData.resolutionDetails}
                  onChange={(e) => setFormData({ ...formData, resolutionDetails: e.target.value })}
                  placeholder="أدخل تفاصيل الحل هنا..."
                  rows={4}
                />
              </FormControl>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          <Button
            bg={primaryColor}
            color={secondaryColor}
            _hover={{ bg: `${primaryColor}dd` }}
            ml={3}
            onClick={onUpdate}
          >
            حفظ التغييرات
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Component: ReclamationDeleteModal
const ReclamationDeleteModal = ({ isOpen, onClose, reclamation, onConfirm }) => {
  if (!reclamation) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent dir="rtl" fontFamily="Tajawal, sans-serif">
        <ModalHeader color="red.500">تأكيد الحذف</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>
            هل أنت متأكد أنك تريد حذف الشكوى "{reclamation.title}"؟
            لا يمكن التراجع عن هذا الإجراء.
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          <Button colorScheme="red" ml={3} onClick={onConfirm}>حذف</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Component: AddNoteModal
const AddNoteModal = ({
  isOpen,
  onClose,
  note,
  setNote,
  isInternal,
  setIsInternal,
  onAdd,
  primaryColor,
  secondaryColor
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent dir="rtl" fontFamily="Tajawal, sans-serif">
        <ModalHeader color={secondaryColor}>إضافة ملاحظة إدارية</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl>
              <FormLabel>الملاحظة</FormLabel>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="أدخل الملاحظة هنا..."
                rows={4}
              />
            </FormControl>
            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="internal-note" mb="0">
                ملاحظة داخلية (غير مرئية للمستخدم)
              </FormLabel>
              <Switch
                id="internal-note"
                isChecked={isInternal}
                onChange={(e) => setIsInternal(e.target.checked)}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          <Button
            bg={primaryColor}
            color={secondaryColor}
            _hover={{ bg: `${primaryColor}dd` }}
            ml={3}
            onClick={onAdd}
          >
            إضافة
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

// Component: AddCommunicationModal
const AddCommunicationModal = ({
  isOpen,
  onClose,
  communication,
  setCommunication,
  onAdd,
  primaryColor,
  secondaryColor
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent dir="rtl" fontFamily="Tajawal, sans-serif">
        <ModalHeader color={secondaryColor}>إضافة سجل تواصل</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl>
              <FormLabel>نوع التواصل</FormLabel>
              <Select
                value={communication.type}
                onChange={(e) => setCommunication({ ...communication, type: e.target.value })}
              >
                <option value="platform_message">رسالة داخل المنصة</option>
                <option value="email">بريد إلكتروني</option>
                <option value="phone">مكالمة هاتفية</option>
                <option value="whatsapp">واتساب</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>الاتجاه</FormLabel>
              <Select
                value={communication.direction}
                onChange={(e) => setCommunication({ ...communication, direction: e.target.value })}
              >
                <option value="outgoing">صادر (من الإدارة للمستخدم)</option>
                <option value="incoming">وارد (من المستخدم للإدارة)</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>المحتوى</FormLabel>
              <Textarea
                value={communication.content}
                onChange={(e) => setCommunication({ ...communication, content: e.target.value })}
                placeholder="أدخل محتوى التواصل هنا..."
                rows={4}
              />
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>إلغاء</Button>
          <Button
            bg={primaryColor}
            color={secondaryColor}
            _hover={{ bg: `${primaryColor}dd` }}
            ml={3}
            onClick={onAdd}
          >
            إضافة
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const AdminReclamationsManagerWrapper = () => {
  return (
    <AdminReclamationsManager />
  );
};

export default AdminReclamationsManagerWrapper;

